import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
// import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FormatShapesIcon from "@mui/icons-material/FormatShapes";
import FullScreen from "@mui/icons-material/Fullscreen";
import FullScreenExit from "@mui/icons-material/FullscreenExit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import ViewStreamOutlinedIcon from "@mui/icons-material/ViewStreamOutlined";
import { Box, IconButton, Switch, Toolbar } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { MaterialReactTable, MaterialReactTableProps, MRT_ColumnDef } from "material-react-table";
import { ClientDealTableRow, DealTableColumn, Nullable, ReportTableColumn, ReportTableData } from "store/types";

import { GroupByMenu } from "..";
import { dateRangeOptions } from "../../../constants";
import { useReportsQueriesSelector } from "../../../store";
import { SelectDateRange } from "../../forms";
import { TableCell } from "../Measures-table/TableCell";
import { AutocompleteMeasures } from "../Set-table-columns/AutocompleteMeasures";
import { SetTableGroupBy } from "../Set-table-group-by/SetTableGroupBy";
import { SetTableReportBy } from "../Set-table-report-by/SetTableReportBy";

import "./ClassicTable.styles.scss";

export const tableProps: Partial<MaterialReactTableProps<ClientDealTableRow>> = {
  enablePagination: false,
  enableDensityToggle: false,
  muiTablePaperProps: {
    sx: {
      boxShadow: "none",
      "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
        "&:first-child": {
          overflow: "visible !important"
        }
      }
    }
  },
  muiTableBodyCellProps: { sx: { borderColor: "gray.50" } },
  muiTableHeadRowProps: { sx: { backgroundColor: "white" } },
  muiTableBodyRowProps: { sx: { backgroundColor: "white" } },
  muiTableHeadCellProps: { sx: { borderColor: "gray.50", " .MuiDivider-root": { borderWidth: 1 } } },
  icons: {
    ArrowDownwardIcon: (props: { className: string }) => (
      <div className="measures-table__sort-icons">
        <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
        <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
      </div>
    )
    // DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
  },
  muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
  muiExpandAllButtonProps: { color: "primary" },
  muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } }
};

interface MeasuresTableProps {
  isLoading: boolean;
  data?: Nullable<ReportTableData>;
  columns: ReportTableColumn[];
  originalCurrency: boolean;
  onChangeColumns?: (columns: ReportTableColumn[]) => void;
  onChangeOriginalCurrency?: (value: boolean) => void;
  title?: string;
  readonly?: boolean;
  stickyHeader?: boolean;
  enableColumnOrdering?: boolean;
}

interface ToolbarButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  renderIcon: React.JSX.Element;
  tooltip: string;
}

const ToolbarButton = ({ onClick, renderIcon, tooltip }: ToolbarButtonProps) => {
  return (
    <MaterialTooltip isShow title={tooltip} placement="top">
      <IconButton onClick={onClick}>{renderIcon}</IconButton>
    </MaterialTooltip>
  );
};

export const ClassicTable: React.FC<MeasuresTableProps> = ({
  isLoading,
  data,
  columns,
  onChangeColumns,
  originalCurrency,
  onChangeOriginalCurrency,
  title = "Measures",
  readonly = false,
  stickyHeader = false,
  enableColumnOrdering = false
}) => {
  const { dateRange: defaultDateRange } = useReportsQueriesSelector();

  const generateUniqueKey = (baseKey: string, existingColumns: ReportTableColumn[]): string => {
    let uniqueKey = baseKey;
    let counter = 0;

    while (existingColumns.some((col) => col.key === uniqueKey)) {
      counter++;
      uniqueKey = `${baseKey}${"-dup".repeat(counter)}`;
    }

    return uniqueKey;
  };

  const handleAddColumn = () => {
    if (onChangeColumns) {
      const aggregation = "";
      // const baseKey = aggregation.toLowerCase();
      const uniqueKey = generateUniqueKey(aggregation, columns);
      onChangeColumns([...columns, { aggregation, dateRange: { value: "ytd" }, key: uniqueKey }]);
    }
  };

  const handelRemoveColumn = (clientCol: ReportTableColumn) => {
    const clientColIndex = columns.findIndex((c) => c === clientCol);

    if (columns.length > 1) {
      onChangeColumns?.([...columns.slice(0, clientColIndex), ...columns.slice(clientColIndex + 1)]);
    }
  };

  const getColumnProps = (
    col: DealTableColumn | undefined,
    clientCol: ReportTableColumn,
    colIndex: number
  ): MRT_ColumnDef<ClientDealTableRow> => {
    const dateRange = clientCol.dateRange || defaultDateRange;
    const dateRangeTitle = dateRangeOptions.find((dr) => dr.value === dateRange.value)?.text || "";
    return {
      header: readonly ? dateRangeTitle : "",
      id: col?.key || colIndex.toString(),
      sortingFn: (rowA, rowB, columnId) => {
        const isTotalA = rowA.original.title === "Total";
        const isTotalB = rowB.original.title === "Total";
        if (isTotalA || isTotalB) {
          if (isTotalA && isTotalB) return 0;
          const isDesc =
            rowA.getAllCells().find((c) => c.column.id === columnId)?.column.getIsSorted() === "desc";
          return isTotalA ? (isDesc ? 1 : -1) : isDesc ? -1 : 1;
        }
        const a = rowA.getValue<number>(columnId) ?? 0;
        const b = rowB.getValue<number>(columnId) ?? 0;
        return a - b;
      },
      minSize: 250,
      accessorFn: col ? (row) => row.valueData[col.key] : undefined,
      Header: readonly
        ? undefined
        : () => {
            return (
              <div className="classic-table__header">
                <SelectDateRange
                  formControlProps={{ sx: { flexGrow: 1, paddingRight: "30px", minWidth: "100px" } }}
                  selectProps={{
                    startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
                  }}
                  label=""
                  value={dateRange}
                  onChange={(dateRange) =>
                    onChangeColumns?.([
                      ...columns.slice(0, colIndex),
                      { ...clientCol, dateRange },
                      ...columns.slice(colIndex + 1)
                    ])
                  }
                />
                {columns.length > 1 && (
                  <CancelRoundedIcon
                    className="classic-table__remove-column"
                    onClick={(e) => {
                      e.stopPropagation();
                      handelRemoveColumn(clientCol);
                    }}
                  />
                )}
              </div>
            );
          },
      Cell: ({ row, column }) =>
        col && (
          <TableCell
            id={column.id}
            value={row.original.valueData[col.key]}
            postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
          />
        ),
      AggregatedCell: ({ row, column }) =>
        col && (
          <TableCell
            id={column.id}
            value={row.original.valueData[col.key]}
            postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
            aggregated
          />
        )
    };
  };

  const mrtColumns = useMemo(() => {
    if (!data) {
      return [];
    }

    const mrtColumns: MRT_ColumnDef<ClientDealTableRow>[] = [];

    columns.forEach((clientCol, index) => {
      const col = mrtColumns.find((col) => col.id === clientCol.aggregation);
      if (col) {
        col.columns?.push(getColumnProps(data.columns[index], clientCol, index));
      } else {
        mrtColumns.push({
          header: readonly ? data.columns[index].title : "",
          id: clientCol.aggregation || `NO_AGG_${index}_${new Date().getTime()}`,
          minSize: 250,
          Header: readonly
            ? undefined
            : () => {
                return (
                  <AutocompleteMeasures
                    label=""
                    queries={{ measures: [clientCol.aggregation] }}
                    disableClearable
                    multiple={false}
                    setQueryValue={(field, value) => {
                      if (value.length) {
                        onChangeColumns?.(
                          columns.map((column) =>
                            column.aggregation === clientCol.aggregation
                              ? { dateRange: column.dateRange, aggregation: value[0] as string }
                              : column
                          )
                        );
                      }
                    }}
                    showGroups={["measures", "calculatedFields"]}
                  />
                );
              },
          columns: [getColumnProps(data.columns[index], clientCol, index)]
        });
      }
    });

    mrtColumns.unshift({
      header: "",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false,
      Cell: ({ cell, row }) => (
        <TableCell
          indentation={row.getParentRow()?.original.deal_id ? 2 : 0}
          dealId={row.original.deal_id}
          value={cell.getValue() as string}
          formatDate
        />
      ),
      AggregatedCell: ({ cell, row }) => (
        <TableCell dealId={row.original.deal_id} value={cell.getValue() as string} aggregated formatDate />
      )
    } as MRT_ColumnDef<ClientDealTableRow>);

    mrtColumns.unshift({
      header: "",
      id: "pme_warning",
      accessorKey: "pme_warning",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableResizing: false,
      enableSorting: false,
      enableColumnOrdering: false,
      size: 20,
      muiTableHeadCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px",
          borderColor: "#E8E8E8"
        }
      },
      muiTableBodyCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px",
          borderColor: "#E8E8E8"
        }
      },
      Cell: ({ row }) =>
        row.original.pme_warning && (
          <MaterialTooltip isShow title={row.original.pme_warning} centerText placement="top">
            <InfoOutlinedIcon fontSize="sm" sx={{ fill: "#071F35" }} />
          </MaterialTooltip>
        )
    } as MRT_ColumnDef<ClientDealTableRow>);

    return mrtColumns;
  }, [data, columns, defaultDateRange]);

  return (
    <Box sx={{ width: "100%" }} className="classic-table measures-table">
      {!readonly && (
        <IconButton onClick={handleAddColumn} disableRipple className="classic-table__add-column">
          <AddIcon />
        </IconButton>
      )}
      <MaterialReactTable
        {...tableProps}
        key={(!!data).toString()} // That recalculates the initial state once the data is loaded
        enableStickyHeader={stickyHeader}
        muiTableHeadCellProps={{ ...tableProps.muiTableHeadCellProps, className: "classic-table__head-cell" }}
        muiTableBodyCellProps={{ ...tableProps.muiTableBodyCellProps, className: "classic-table__body-cell" }}
        muiTableHeadRowProps={{ ...tableProps.muiTableHeadRowProps, className: "classic-table__head-row" }}
        renderTopToolbar={({ table }) => {
          return (
            <Toolbar className="measures-table__toolbar">
              <span className="title">{title}</span>
              <div className="measures-table__toolbar-right">
                <ToolbarButton
                  tooltip={"Toggle Expand"}
                  onClick={() => table.setIsFullScreen(!table.getState().isFullScreen)}
                  renderIcon={table.getState().isFullScreen ? <FullScreenExit /> : <FullScreen />}
                />
                {!readonly && (
                  <>
                    <GroupByMenu tooltip="Group Rows" icon={<FormatShapesIcon fontSize="md" />}>
                      <SetTableGroupBy isDealView={false} />
                    </GroupByMenu>
                    <GroupByMenu icon={<ViewStreamOutlinedIcon />} tooltip="Configure Rows">
                      <SetTableReportBy />
                    </GroupByMenu>
                    {onChangeOriginalCurrency && (
                      <div className="input-wrap">
                        <span className="input-wrap__title">Original Currency</span>
                        <Switch
                          checked={originalCurrency}
                          onChange={(e, checked) => {
                            onChangeOriginalCurrency(checked);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </Toolbar>
          );
        }}
        getRowId={(row) => row.id!}
        columns={mrtColumns}
        filterFromLeafRows
        getSubRows={(row) => row.children}
        data={data?.rows || []}
        state={{ isLoading: !data || isLoading, columnOrder: [] }}
        enableExpanding
        enableColumnOrdering={enableColumnOrdering}
        enableColumnResizing
        columnResizeDirection="ltr"
        columnResizeMode="onChange"
        enableColumnActions={false}
        layoutMode="grid"
        // {...tableProps}
      />
    </Box>
  );
};
